/**
 * TESTIMONIALS PAGE
 * Showcases patient testimonials, video stories, and Google reviews
 * World-class design for world-class surgeon
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSite } from '../contexts/SiteContext';
import { getTestimonials } from '../services/api';
import { 
  Star, 
  Play, 
  ExternalLink, 
  Quote,
  CheckCircle2,
  Youtube
} from 'lucide-react';

// Video testimonials from YouTube
const videoTestimonials = [
  {
    id: 1,
    title: 'Shoulder Arthroscopy: Regaining Function',
    description: 'How minimally invasive shoulder surgery helped restore mobility.',
    thumbnail: 'https://img.youtube.com/vi/VIDEO_ID_1/maxresdefault.jpg',
    category: 'Shoulder'
  },
  {
    id: 2,
    title: 'ACL Reconstruction: Back to Sports',
    description: 'Knee ligament reconstruction and rehabilitation success.',
    thumbnail: 'https://img.youtube.com/vi/VIDEO_ID_2/maxresdefault.jpg',
    category: 'Knee'
  },
  {
    id: 3,
    title: 'Rotator Cuff Repair: Shoulder Strength Restored',
    description: 'Tendon repair and physical therapy results.',
    thumbnail: 'https://img.youtube.com/vi/VIDEO_ID_3/maxresdefault.jpg',
    category: 'Shoulder'
  },
  {
    id: 4,
    title: 'Elbow Surgery: Pain Relief & Full Motion',
    description: 'Complex elbow repair with excellent recovery outcomes.',
    thumbnail: 'https://img.youtube.com/vi/VIDEO_ID_4/maxresdefault.jpg',
    category: 'Elbow'
  },
  {
    id: 5,
    title: 'Shoulder Dislocation Repair (Bankart)',
    description: 'Arthroscopic shoulder stabilisation — no more dislocations.',
    thumbnail: 'https://img.youtube.com/vi/VIDEO_ID_5/maxresdefault.jpg',
    category: 'Shoulder'
  },
  {
    id: 6,
    title: 'Wrist Surgery: Restoring Grip & Dexterity',
    description: 'Ligament repair improving wrist strength and flexibility.',
    thumbnail: 'https://img.youtube.com/vi/VIDEO_ID_6/maxresdefault.jpg',
    category: 'Hand & Wrist'
  }
];

// Written testimonials with full details
const writtenTestimonials = [
  {
    id: 1,
    name: 'Raghu Ram',
    rating: 5,
    text: 'Very experienced and highly qualified doctor. Explained the root cause clearly and gave confidence to my child during shoulder surgery. Grateful to Dr. Reddy and team.',
    procedure: 'Shoulder Surgery',
    source: 'Google',
    verified: true
  },
  {
    id: 2,
    name: 'Renuka Reddy Rachupalli',
    rating: 5,
    text: "Incredibly grateful for the outstanding care during my mother's surgery. Dr. Reddy showed exceptional skill and compassion throughout.",
    procedure: 'Surgery',
    source: 'Google',
    verified: true
  },
  {
    id: 3,
    name: 'Kunal Mahendru',
    rating: 5,
    text: "Suffering from a rare nerve disease, all doctors suggested surgery—but Dr. Reddy identified the real issue and guided me to recovery through physiotherapy. A true professional who puts patients first.",
    procedure: 'Conservative Treatment',
    source: 'Google',
    verified: true
  },
  {
    id: 4,
    name: 'Sarika Otle',
    rating: 5,
    text: 'Absolutely top and compassionate doctor. Humane, skilled, and genuinely cares for patients. One of the best.',
    procedure: 'General Care',
    source: 'Google',
    verified: true
  },
  {
    id: 5,
    name: 'Shyam Kumar',
    rating: 5,
    text: "Thank you for the excellent care and surgery. Dr. Reddy's attention and guidance made my recovery smooth.",
    procedure: 'Surgery',
    source: 'Google',
    verified: true
  },
  {
    id: 6,
    name: 'Rama Krishna U',
    rating: 5,
    text: 'Underwent rotator cuff repair. Dr. Reddy is patient and supportive; had an excellent recovery and regained full movement.',
    procedure: 'Rotator Cuff Repair',
    source: 'Google',
    verified: true
  },
  {
    id: 7,
    name: 'Vikas Soni',
    rating: 5,
    text: 'Visited for shoulder dislocation; underwent arthroscopic Bankart repair. Excellent recovery and professional guidance.',
    procedure: 'Arthroscopic Bankart Repair',
    source: 'Google',
    verified: true
  },
  {
    id: 8,
    name: 'Pavankumar Akula',
    rating: 5,
    text: 'Explained everything clearly and treated with great kindness. Highly recommend for shoulder, hand, and elbow care.',
    procedure: 'Consultation',
    source: 'Google',
    verified: true
  },
  {
    id: 9,
    name: 'Anupama Raikanti',
    rating: 5,
    text: "Handled my parents' knee replacements with great skill and compassion. Very professional and dependable.",
    procedure: 'Knee Replacement',
    source: 'Google',
    verified: true
  },
  {
    id: 10,
    name: 'Prasad SVV',
    rating: 5,
    text: 'Underwent arthroscopy for supraspinatus tendon repair. Doctor analyzed MRI in detail and guided recovery perfectly.',
    procedure: 'Arthroscopy - Supraspinatus Repair',
    source: 'Google',
    verified: true
  }
];

export const TestimonialsPage = () => {
  const { theme, doctor } = useSite();
  const [testimonials, setTestimonials] = useState(writtenTestimonials);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getTestimonials();
        if (data && data.length > 0) {
          setTestimonials(data);
        }
      } catch (error) {
        console.log('Using default testimonials');
      }
    };
    fetchTestimonials();
  }, []);

  const googleReviewsUrl = `https://www.google.com/search?q=Dr.+Deepthi+Nandan+Reddy+Reviews`;

  return (
    <div className="min-h-screen" data-testid="testimonials-page">
      {/* Hero Section */}
      <section 
        className="py-20 text-white relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${theme.primaryHex} 0%, #1e3a5f 100%)` }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-[200px] font-serif">"</div>
          <div className="absolute bottom-10 right-10 text-[200px] font-serif rotate-180">"</div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Patient Success Stories
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-8">
              Real experiences from patients treated by {doctor.name}
            </p>
            
            {/* Rating Summary */}
            <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <div className="text-left">
                <p className="text-3xl font-bold">5.0</p>
                <p className="text-sm opacity-80">Based on 100+ Google Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">
                Video Testimonials
              </h2>
              <p className="text-gray-400">
                Watch real-life recovery stories from our patients
              </p>
            </div>
            <a 
              href={doctor.socialMedia?.youtube || 'https://www.youtube.com/@DrDNReddy'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                <Youtube className="w-5 h-5 mr-2" />
                View All on YouTube
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videoTestimonials.map((video) => (
              <a
                key={video.id}
                href={doctor.socialMedia?.youtube || 'https://www.youtube.com/@DrDNReddy'}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="bg-gray-800 border-gray-700 overflow-hidden hover:border-red-500 transition-colors">
                  <div className="aspect-video relative bg-gray-700">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                    </div>
                    <span 
                      className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: theme.primaryHex }}
                    >
                      {video.category}
                    </span>
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg line-clamp-1">
                      {video.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 text-sm line-clamp-2">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Written Reviews Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Verified Patient Reviews
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Read what our patients have to say about their experience with {doctor.name}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id || index} 
                className="relative hover:shadow-xl transition-shadow border-l-4"
                style={{ borderLeftColor: theme.primaryHex }}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {testimonial.name}
                        {testimonial.verified && (
                          <CheckCircle2 className="w-5 h-5 text-blue-500" />
                        )}
                      </CardTitle>
                      <p className="text-sm text-gray-500">{testimonial.procedure}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Quote className="w-8 h-8 absolute -top-2 -left-2 text-gray-200" />
                    <p className="text-gray-700 leading-relaxed pl-6">
                      {testimonial.text}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <img 
                      src="https://www.google.com/favicon.ico" 
                      alt="Google" 
                      className="w-4 h-4"
                    />
                    <span className="text-xs text-gray-500">Verified Google Review</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* View More on Google */}
          <div className="text-center mt-12">
            <a href={googleReviewsUrl} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg"
                variant="outline"
                className="border-2"
                style={{ borderColor: theme.primaryHex, color: theme.primaryHex }}
              >
                <img 
                  src="https://www.google.com/favicon.ico" 
                  alt="Google" 
                  className="w-5 h-5 mr-2"
                />
                Read All Reviews on Google
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Live Google Reviews Embed Section */}
      <section className="py-20" style={{ backgroundColor: theme.lightBgHex }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Live Google Reviews
            </h2>
            <p className="text-lg text-gray-600">
              See our latest reviews directly from Google
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden shadow-xl">
              <CardContent className="p-0">
                {/* Google Reviews Widget Placeholder */}
                <div className="bg-white p-8 text-center">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <img 
                      src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png" 
                      alt="Google" 
                      className="h-8"
                    />
                    <span className="text-2xl font-semibold text-gray-800">Reviews</span>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="text-5xl font-bold text-gray-900">5.0</span>
                    <div className="flex flex-col items-start">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      <span className="text-sm text-gray-500">Based on 100+ reviews</span>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6">
                    {doctor.name} maintains a perfect 5-star rating on Google with over 100 verified patient reviews.
                  </p>

                  <a href={googleReviewsUrl} target="_blank" rel="noopener noreferrer">
                    <Button 
                      size="lg"
                      style={{ backgroundColor: theme.primaryHex }}
                      className="text-white"
                    >
                      View All Google Reviews
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: theme.primaryHex }}>
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Recovery Journey?
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied patients who trusted {doctor.name} with their care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white hover:bg-gray-100" style={{ color: theme.primaryHex }}>
                Book Your Appointment
              </Button>
            </a>
            <a href={doctor.whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                💬 WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
